import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';

export const ServiceDetailsSlider = ({ details }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % details.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [details.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? details.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % details.length);
  };

  const getVisibleDetails = () => {
    const visible = [];
    for (let i = 0; i < 2; i++) {
      const index = (currentIndex + i) % details.length;
      visible.push({ ...details[index], key: index });
    }
    return visible;
  };

  return (
    <div className="relative" data-testid="service-details-slider">
      {/* Desktop - 2 details */}
      <div className="hidden md:grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {getVisibleDetails().map((detail, idx) => (
          <motion.div
            key={detail.key}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
            className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg hover:border-red-100 transition-all duration-300"
          >
            <div className="flex gap-4">
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3 h-fit flex-shrink-0">
                {detail.icon}
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                  {detail.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {detail.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Mobile - Single detail */}
      <div className="md:hidden">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg hover:border-red-100 transition-all duration-300"
        >
          <div className="flex gap-4">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3 h-fit flex-shrink-0">
              {details[currentIndex].icon}
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                {details[currentIndex].title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {details[currentIndex].description}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-center gap-4 mt-8">
        <Button
          onClick={goToPrevious}
          variant="outline"
          size="icon"
          className="rounded-full h-12 w-12 border-slate-200 hover:bg-red-50 hover:border-red-200"
          data-testid="service-details-prev-button"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Button>
        <Button
          onClick={goToNext}
          variant="outline"
          size="icon"
          className="rounded-full h-12 w-12 border-slate-200 hover:bg-red-50 hover:border-red-200"
          data-testid="service-details-next-button"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {details.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-8 bg-red-600' : 'w-2 bg-slate-300'
            }`}
            data-testid={`service-details-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};
